import React, { useState, useEffect } from "react";
import Sidebar from "../../components/Admin/Sidebar";
import Header from "../../components/Admin/Header";
import api from "../../api/axois";
import styles from "./OrderManagement.module.css";
import { FaUtensils, FaUser, FaPhoneAlt, FaMapMarkerAlt, FaBed, FaClock } from "react-icons/fa";

// Backend status enum (see ChopNation\KitchenController::updateStatus).
const STATUSES = ["pending", "processing", "prepared", "delivered", "cancelled"];
const statusLabel = (status) => status.charAt(0).toUpperCase() + status.slice(1);

const OrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("All");

  const fetchOrders = async () => {
    try {
      setError(null);
      const res = await api.get("/chopnation/orders");
      setOrders(res.data);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load orders.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const updateStatus = async (orderId, newStatus) => {
    const previous = orders;
    setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
    try {
      await api.patch(`/chopnation/kitchen/orders/${orderId}/status`, { status: newStatus });
    } catch (err) {
      setOrders(previous);
      alert(err.response?.data?.message || "Failed to update order status.");
    }
  };

  const filteredOrders = orders.filter(o =>
    filter === "All" ? true : o.status === filter
  );

  const printKOT = (order) => {
    const printWindow = window.open("", "_blank", "width=380,height=600");
    if (!printWindow) {
      alert("Please allow pop-ups to print the KOT.");
      return;
    }

    const orderTime = new Date(order.created_at).toLocaleString([], {
      dateStyle: "medium",
      timeStyle: "short"
    });

    const itemsHtml = order.items.map(item => `
      <tr>
        <td class="qty">${item.quantity}x</td>
        <td>
          ${item.item_name}
          ${item.special_instructions ? `<div class="note">Note: ${item.special_instructions}</div>` : ""}
        </td>
      </tr>
    `).join("");

    const locationLine = order.order_type === "delivery"
      ? `Room ${order.room_number || "-"} (Guest)`
      : (order.location || "Pickup");

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>KOT - ORD-${order.id}</title>
          <style>
            * { box-sizing: border-box; }
            body {
              font-family: 'Courier New', monospace;
              width: 300px;
              margin: 0 auto;
              padding: 16px;
              color: #000;
            }
            h1 { font-size: 16px; text-align: center; margin: 0 0 4px; }
            .subtitle { text-align: center; font-size: 11px; margin-bottom: 12px; }
            hr { border: none; border-top: 1px dashed #000; margin: 8px 0; }
            .meta { font-size: 12px; margin-bottom: 4px; }
            table { width: 100%; border-collapse: collapse; margin-top: 8px; }
            td { font-size: 13px; padding: 4px 0; vertical-align: top; }
            .qty { width: 40px; font-weight: bold; }
            .note { font-size: 11px; font-style: italic; color: #333; }
            .footer { text-align: center; font-size: 11px; margin-top: 12px; }
          </style>
        </head>
        <body>
          <h1>KITCHEN ORDER TICKET</h1>
          <div class="subtitle">AURELIA DINING</div>
          <hr />
          <div class="meta"><strong>Order:</strong> ORD-${order.id}</div>
          <div class="meta"><strong>Time:</strong> ${orderTime}</div>
          <div class="meta"><strong>Customer:</strong> ${order.customer_name || "Guest"}</div>
          <div class="meta"><strong>Location:</strong> ${locationLine}</div>
          <hr />
          <table>${itemsHtml}</table>
          <hr />
          <div class="footer">-- End of Ticket --</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    // document.write()'d popups don't reliably fire onload, so give the
    // browser a tick to paint before invoking the print dialog.
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  return (
    <div className={styles.adminContainer}>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className={styles.mainContent}>
        <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
        
        <div className={styles.contentWrapper}>
          <div className={styles.pageHeader}>
            <div className={styles.headerTitle}>
              <h1>Food Order Management</h1>
              <p>Monitor and coordinate digital orders from guests and external customers.</p>
            </div>
            
            <div className={styles.filterBar}>
              {["All", ...STATUSES].map(s => (
                <button
                  key={s}
                  className={filter === s ? styles.activeFilter : styles.filterBtn}
                  onClick={() => setFilter(s)}
                >
                  {s === "All" ? s : statusLabel(s)}
                </button>
              ))}
            </div>
          </div>

          {error && <div className={styles.loader}>{error}</div>}

          {loading ? (
            <div className={styles.loader}>Loading orders...</div>
          ) : (
            <div className={styles.orderGrid}>
              {filteredOrders.map(order => (
                <div key={order.id} className={styles.orderCard}>
                  <div className={styles.cardHeader}>
                    <span className={styles.orderId}>ORD-{order.id}</span>
                    <span className={`${styles.statusBadge} ${styles[order.status]}`}>
                      {statusLabel(order.status)}
                    </span>
                  </div>

                  <div className={styles.cardBody}>
                    <div className={styles.customerSection}>
                      <h3><FaUser /> {order.customer_name || "Guest"}</h3>
                      <p><FaPhoneAlt /> {order.customer_phone}</p>
                      {order.order_type === "delivery" ? (
                        <p className={styles.location}><FaBed /> Room {order.room_number} (Guest)</p>
                      ) : (
                        <p className={styles.location}><FaMapMarkerAlt /> {order.location}</p>
                      )}
                    </div>

                    <div className={styles.itemsSection}>
                      <h4><FaUtensils /> Items</h4>
                      <ul>
                        {order.items.map((item) => (
                          <li key={item.id}>
                            <span>{item.quantity}x {item.item_name}</span>
                            <span>₦{Number(item.subtotal).toLocaleString()}</span>
                          </li>
                        ))}
                      </ul>
                      <div className={styles.totalRow}>
                        <span>Total Paid</span>
                        <span>₦{Number(order.total_amount).toLocaleString()}</span>
                      </div>
                    </div>

                    <div className={styles.timeSection}>
                      <FaClock /> {new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>

                  <div className={styles.cardActions}>
                    <select
                      value={order.status}
                      onChange={(e) => updateStatus(order.id, e.target.value)}
                    >
                      {STATUSES.map(s => (
                        <option key={s} value={s}>{statusLabel(s)}</option>
                      ))}
                    </select>
                    <button className={styles.actionBtn} onClick={() => printKOT(order)}>Print KOT</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderManagement;
